import * as Yup from 'yup';
import * as React from 'react';
import { useSnackbar } from 'notistack';
import { Skeleton } from '@mui/material';
import { useParams } from 'react-router-dom';
import { Formik, Field, FieldProps } from 'formik';
import { useMutation, useQueryClient } from 'react-query';

import { useApi } from '../../shared/context';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { FormikUtils, HtmlUtils } from '../../shared/utils';
import { QueryKeys, useDataRoomFolderHierarchyQuery, useRequiredDocumentTemplatesQuery } from '../../shared/hooks';
import { Guid, LoanApplicationTask, LoanApplicationTaskTemplate } from '../../shared/models';

import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroHierarchyFolderTree,
	CerebroInput,
	CerebroInputProps,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroStyledBox,
	CerebroText,
	CerebroTextarea,
} from '../../shared/components';
import { getApiResponseData } from '../../shared/utils/axios';

export const RequestDocumentDrawer: React.FC<RequestDocumentDrawerProps> = ({
	open = false,
	dataRoomGuid,
	onClose,
}) => {
	const api = useApi();
	const { guid } = useParams<'guid'>();

	const snackbar = useSnackbar();
	const queryClient = useQueryClient();

	const ref = React.useRef<HTMLDivElement>(null);

	const templatesQuery = useRequiredDocumentTemplatesQuery();
	const foldersQuery = useDataRoomFolderHierarchyQuery(dataRoomGuid);

	const documentMutation = useMutation(
		async ({ values }: RequestDocumentMutationVariables) => {
			const { data, error } = await getApiResponseData<LoanApplicationTask>(
				api.post(CerebroApiEndpoints.RequiredDocuments.RequiredDocument(guid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QueryKeys.RequiredDocuments.Root(guid));
				snackbar.enqueueSnackbar('Required document saved.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={open} onClose={onClose}>
			<CerebroHeading size="h2" component="h3" gutterBottom>
				Request a Document
			</CerebroHeading>
			<CerebroText variant="small" sx={{ width: 450 }}>
				Request a document from the borrower by indicating the type of document you wish to request and
				it&apos;s title.
			</CerebroText>

			<Formik
				enableReinitialize
				validationSchema={RequestDocumentSchema}
				onSubmit={FormikUtils.MutateOnSubmit(documentMutation.mutate)}
				initialValues={{
					taskType: '-1',
					isCustomFolder: false,
					title: '',
					summary: '',
					folderGuid: '',
				}}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit, setFieldValue }) => (
					<CerebroForm
						flatIndicator
						onSubmit={handleSubmit}
						sx={{ paddingRight: 3, width: { xs: '100%', md: 400 } }}
						scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(ref.current)}
					>
						<CerebroLabel required htmlFor="taskType" sx={{ marginTop: 3 }} gutterBottom>
							Type
						</CerebroLabel>

						<CerebroText variant="hint" gutterBottom>
							The title will initially be the same as the type but you may customize it if needed.
						</CerebroText>

						{templatesQuery.isLoading && <Skeleton height={60} />}

						{templatesQuery.isSuccess && (
							<>
								<Field
									id="taskType"
									as={CerebroDropdown}
									name="taskType"
									error={FormikUtils.IsInvalid('taskType', errors, touched)}
									disabled={isSubmitting}
									placeholder=""
									onChange={(e: any) => {
										const changed = e.target.value as Guid;
										setFieldValue('taskType', changed);
										const template = templatesQuery.data.filter(w => w.guid == changed);
										setFieldValue(
											'isCustomFolder',
											template && template.length > 0 && template[0].isCustomFolder
										);
									}}
								>
									<CerebroDropdownMenuItem value="-1">
										Select a document type...
									</CerebroDropdownMenuItem>
									{templatesQuery.data.map(template => (
										<CerebroDropdownMenuItem key={template.guid} value={template.guid}>
											{template.title}
										</CerebroDropdownMenuItem>
									))}
								</Field>
								{FormikUtils.IsInvalid('taskType', errors, touched) && (
									<CerebroText variant="error">{errors.taskType}</CerebroText>
								)}
							</>
						)}

						<CerebroLabel required htmlFor="title" sx={{ marginTop: 3 }}>
							Title
						</CerebroLabel>
						<Field
							id="title"
							name="title"
							component={DocumentTitleInput}
							templates={templatesQuery.data}
							placeholder="Document title..."
							disabled={isSubmitting}
							error={FormikUtils.IsInvalid('title', errors, touched)}
						/>
						{FormikUtils.IsInvalid('title', errors, touched) && (
							<CerebroText variant="error">{errors.title}</CerebroText>
						)}

						<CerebroLabel htmlFor="description" sx={{ marginTop: 3 }}>
							Description
						</CerebroLabel>
						<Field
							id="summary"
							name="summary"
							as={CerebroTextarea}
							placeholder="Required document description..."
							disabled={isSubmitting}
						/>

						{values.isCustomFolder && (
							<>
								<CerebroLabel htmlFor="description" sx={{ marginTop: 3 }}>
									Data Room Folder
								</CerebroLabel>
								{foldersQuery.isLoading && <Skeleton height={60} />}
								{foldersQuery.isSuccess && (
									<CerebroStyledBox alignitems="left" sx={{ marginTop: 1 }}>
										<CerebroHierarchyFolderTree
											folder={foldersQuery.data}
											selectedGuid={values.folderGuid}
											onSelected={value => (values.folderGuid = value)}
										/>
									</CerebroStyledBox>
								)}
							</>
						)}

						<CerebroPrimaryButton loading={isSubmitting} type="submit" sx={{ marginTop: 6 }}>
							Save Required Document
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

interface RequestDocumentDrawerProps {
	open?: boolean;
	dataRoomGuid: string | undefined;
	onClose: () => void;
}

function DocumentTitleInput({ field, form, meta, templates, ...props }: DocumentTitleInputProps) {
	const { name } = field;

	const {
		values: { taskType },

		setFieldValue,
	} = form;

	React.useEffect(() => {
		const template = templates?.find(template => template.guid === taskType);
		if (template) setFieldValue(name, template.title);
	}, [name, setFieldValue, taskType, templates]);

	return <CerebroInput {...props} {...field} />;
}

type DocumentTitleInputProps = FieldProps<string, RequestDocumentFormValues> &
	CerebroInputProps & { templates?: LoanApplicationTaskTemplate[] };

interface RequestDocumentFormValues {
	title: string;
	taskType: string;
	summary: string;
	isCustomFolder: boolean;
	folderGuid: string;
}

type RequestDocumentMutationVariables = FormikUtils.MutateOnSubmitVariables<RequestDocumentFormValues>;

const RequestDocumentSchema = Yup.object().shape({
	title: Yup.string().required('Please provide a title for the document'),
	taskType: Yup.string().notOneOf(['-1'], 'Please select a document type'),
});
